import {roundDecimalInteger, roundToInterval} from 'powtoon-commons/utils/numbers'
import {MINIMUM_STEP_SIZE} from 'powtoon-document/consts/duration'

export const updatePlaybackSettings = ({
  track,
  trackNewStartTime,
  trackNewEndTime,
  videoDuration,
  playbackSettings,
  isVoiceoverLogicActive,
  playbackOptions = {}
}) => {
  if (!(playbackSettings && videoDuration)) {
    return playbackSettings
  }

  const {startTime: trackCurrentStartTime, endTime: trackCurrentEndTime} = track
  const {startTime: psStartTime, endTime: psEndTime} = playbackSettings
  const {preserveEndTime, shiftStartTimeOnTrim} = playbackOptions

  const deltaStartTime = trackNewStartTime - trackCurrentStartTime
  const deltaEndTime = trackNewEndTime - trackCurrentEndTime
  const durationDelta = roundDecimalInteger(deltaEndTime - deltaStartTime)
  const shiftValue = Math.abs(durationDelta)

  let newStartTime
  let newEndTime

  // Logic for shifting start when trimming slide from the left (Left Trim)
  if (shiftStartTimeOnTrim && durationDelta < 0) {

    // Shift the internal start forward by the amount trimmed from the slide
    newStartTime = getStartTime({
      psStartTime,
      trackCurrentStartTime: 0,
      trackNewStartTime: shiftValue,
      isVoiceoverLogicActive
    })

    // Keep the internal end fixed (preservePlaybackSettingsEndTime: true)
    newEndTime = getEndTime({
      psEndTime,
      trackCurrentEndTime,
      trackNewEndTime,
      videoDuration,
      isVoiceoverLogicActive,
      trackCurrentStartTime,
      trackNewStartTime,
      preservePlaybackSettingsEndTime: true
    })
  }
  else {
    // Standard logic for Track Resize or Move
    if (shiftValue < MINIMUM_STEP_SIZE) {
      const isMeaningfulChangeStartTime = Math.abs(deltaStartTime) > MINIMUM_STEP_SIZE
      const isMeaningfulChangeEndTime = Math.abs(deltaEndTime) > MINIMUM_STEP_SIZE

      // If both edges changed significantly, it's a "Move" action - do not change playback settings
      if (isMeaningfulChangeStartTime && isMeaningfulChangeEndTime) {
        return playbackSettings
      }
    }

    newStartTime = getStartTime({
      psStartTime,
      trackCurrentStartTime,
      trackNewStartTime,
      isVoiceoverLogicActive
    })

    newEndTime = getEndTime({
      psEndTime,
      trackCurrentEndTime,
      trackNewEndTime,
      trackNewStartTime,
      videoDuration,
      isVoiceoverLogicActive,
      trackCurrentStartTime,
      preservePlaybackSettingsEndTime: preserveEndTime
    })
  }

  if (newEndTime - newStartTime < MINIMUM_STEP_SIZE) {
    if (!shiftStartTimeOnTrim && (trackCurrentStartTime < trackNewStartTime)) {
      newStartTime = Math.max(newEndTime - MINIMUM_STEP_SIZE, 0)
    }
    else {
      newEndTime = newStartTime + MINIMUM_STEP_SIZE
    }
  }

  return {
    ...playbackSettings,
    endTime: roundToInterval(newEndTime, MINIMUM_STEP_SIZE),
    startTime: roundToInterval(newStartTime, MINIMUM_STEP_SIZE)
  }
}

export const getDefaultVideoPlaybackSettings = (data, startTime, endTime) => ({
  startTime: 0,
  endTime: getDefaultEndTime(data, startTime, endTime),
  isMuted: false
})

export const getEndTime = ({
  psEndTime,
  trackCurrentEndTime = 1, trackNewEndTime = 1,
  videoDuration, isVoiceoverLogicActive = false,
  trackNewStartTime, trackCurrentStartTime, preservePlaybackSettingsEndTime
}) => {
  
  let calculatedEndTime = psEndTime
  const deltaStartTime = Math.abs(trackCurrentStartTime - trackNewStartTime)
  const deltaEndTime =  Math.abs(trackCurrentEndTime - trackNewEndTime)
  
  if (deltaEndTime || deltaStartTime){
    if (!isVoiceoverLogicActive){
      calculatedEndTime = trackNewEndTime > trackCurrentEndTime?
        psEndTime + deltaEndTime:
        preservePlaybackSettingsEndTime ? psEndTime : psEndTime - deltaEndTime

    } else {
      const trackDurationBiggerThanAssetDuration = Math.abs(trackNewEndTime - trackNewStartTime) > videoDuration
      const playbackEndTimeBiggerThanDuration = psEndTime > videoDuration
      const isSetAssetDuration = trackDurationBiggerThanAssetDuration || playbackEndTimeBiggerThanDuration
      if (isSetAssetDuration ){
        calculatedEndTime = videoDuration
      }
      else {
        const oldDuration = trackCurrentEndTime - trackCurrentStartTime
        const wasDurationSmallerThenOldEndTime =  oldDuration > videoDuration
          
        if (deltaEndTime){
          const refinedDeltaEndTime = wasDurationSmallerThenOldEndTime?
            deltaEndTime - (oldDuration - videoDuration)  :
            deltaEndTime

          calculatedEndTime = trackNewEndTime > trackCurrentEndTime ?
            psEndTime + deltaEndTime :
            psEndTime - refinedDeltaEndTime // the bug is here
        }
        if (deltaStartTime) {
          const refinedDeltaStartTime = wasDurationSmallerThenOldEndTime?
            deltaStartTime - (oldDuration - videoDuration)  :
            deltaStartTime
          calculatedEndTime = trackNewStartTime > trackCurrentStartTime ?
            psEndTime - refinedDeltaStartTime :
            psEndTime + deltaStartTime
        }
      }
      return Math.max(Math.min(calculatedEndTime, videoDuration), 0)
    }
  }
  return Math.max(Math.min(roundDecimalInteger(calculatedEndTime), videoDuration), 0)
}
const getDefaultEndTime = (data, startTime = 0, endTime = 1) => {
  const videoDataDuration = data?.videoData?.duration ?? endTime
  return getValidEndTime(endTime - startTime, videoDataDuration)
}

export const getValidEndTime = (trackNewDuration = 0, videoDataDuration= 0) =>
  roundDecimalInteger(Math.min(Math.max(trackNewDuration, 0), videoDataDuration))

export const getStartTime = ({
  psStartTime, trackCurrentStartTime = 0,
  trackNewStartTime, isVoiceoverLogicActive
}) => {

  if (isVoiceoverLogicActive){
    // on VoiceOver time line the left handle wont effect  playbackSettings.startTime only the endTime
    // see VCP-732 LipSync - Timeline behavior update
    return 0
  }
  const deltaStartTime = Math.abs( trackCurrentStartTime - trackNewStartTime) || 0
  const calculatedStartTime = trackCurrentStartTime > trackNewStartTime?
    psStartTime - deltaStartTime:
    psStartTime + deltaStartTime

  return Math.max(roundDecimalInteger(calculatedStartTime), 0)
}
